import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AIModels = () => {
  const aiModels = [
    {
      service: "Generación de Texto",
      models: [
        { name: "GPT-5.2", provider: "OpenAI", capabilities: "Última generación multimodal disponible" },
        { name: "GPT-4.1", provider: "OpenAI", capabilities: "Razonamiento avanzado y contexto extendido" },
        { name: "Claude Sonnet 4.5", provider: "Anthropic", capabilities: "Redacción y análisis extensos" },
        { name: "Gemini 3", provider: "Google", capabilities: "Multimodal, contexto amplio" }
      ],
      icon: "article"
    },
    {
      service: "Generación de Imágenes",
      models: [
        { name: "gpt-image-1", provider: "OpenAI", capabilities: "Generación de imágenes de última generación" },
        { name: "Nano Banana (Gemini)", provider: "Google", capabilities: "Generación y edición de imágenes vía Gemini" },
        { name: "DALL-E 3", provider: "OpenAI", capabilities: "Generación de imágenes de alta calidad (versión previa)" }
      ],
      icon: "image"
    },
    {
      service: "Chatbot Básico",
      models: [
        { name: "GPT-4.1 mini", provider: "OpenAI", capabilities: "Respuestas rápidas y económicas" }
      ],
      icon: "chat"
    },
    {
      service: "Chatbot Estándar",
      models: [
        { name: "GPT-5.1", provider: "OpenAI", capabilities: "Análisis profundo con contexto amplio" },
        { name: "Claude Sonnet 4.5", provider: "Anthropic", capabilities: "Razonamiento y creatividad" }
      ],
      icon: "smart_toy"
    },
    {
      service: "Chatbot Avanzado",
      models: [
        { name: "GPT-5.2", provider: "OpenAI", capabilities: "Máxima capacidad y contexto extendido" },
        { name: "Claude Sonnet 4.6", provider: "Anthropic", capabilities: "Análisis técnico avanzado" },
        { name: "Gemini 3", provider: "Google", capabilities: "Multimodal, contexto amplio" },
        { name: "DeepSeek V3", provider: "DeepSeek", capabilities: "Especializado en código y razonamiento" }
      ],
      icon: "psychology"
    },
    {
      service: "Voz (Text-to-Speech)",
      models: [
        { name: "ElevenLabs", provider: "ElevenLabs", capabilities: "Voces naturales y clonación de voz" },
        { name: "OpenAI TTS", provider: "OpenAI", capabilities: "Múltiples voces de alta calidad" }
      ],
      icon: "mic",
      status: "en-integracion"
    },
    {
      service: "Voz (Speech-to-Text)",
      models: [
        { name: "Whisper", provider: "OpenAI", capabilities: "Transcripción multi-idioma" }
      ],
      icon: "hearing",
      status: "en-integracion"
    },
    {
      service: "Audio y Música",
      models: [
        { name: "Suno", provider: "Suno", capabilities: "Generación de música" }
      ],
      icon: "music_note",
      status: "en-integracion"
    },
    {
      service: "Video",
      models: [
        { name: "Runway Gen-3", provider: "Runway", capabilities: "Generación y edición de video" },
        { name: "Pika", provider: "Pika Labs", capabilities: "Generación de video creativo" }
      ],
      icon: "videocam",
      status: "en-integracion"
    },
    {
      service: "Ciberseguridad Informativa",
      models: [
        { name: "GPT-5.1", provider: "OpenAI", capabilities: "Análisis informativo de vulnerabilidades" },
        { name: "Claude Sonnet 4.5", provider: "Anthropic", capabilities: "Revisión informativa de código" }
      ],
      icon: "security"
    },
    {
      service: "Contabilidad / Finanzas Informativas",
      models: [
        { name: "GPT-5.1", provider: "OpenAI", capabilities: "Apoyo informativo en análisis financiero" }
      ],
      icon: "account_balance"
    },
    {
      service: "Legal Informativo",
      models: [
        { name: "GPT-5.1", provider: "OpenAI", capabilities: "Apoyo informativo en documentos legales" },
        { name: "Claude Sonnet 4.5", provider: "Anthropic", capabilities: "Apoyo informativo en interpretación" }
      ],
      icon: "gavel"
    }
  ];

  const providers = {
    "OpenAI": {
      logo: "🤖",
      description: "Líder en IA generativa",
      website: "openai.com"
    },
    "Anthropic": {
      logo: "🧠",
      description: "IA segura y confiable",
      website: "anthropic.com"
    },
    "Google": {
      logo: "🔍",
      description: "IA multimodal avanzada",
      website: "google.com/ai"
    },
    "DeepSeek": {
      logo: "💻",
      description: "Especialista en código",
      website: "deepseek.com"
    },
    "ElevenLabs": {
      logo: "🎤",
      description: "Síntesis de voz natural",
      website: "elevenlabs.io"
    },
    "Suno": {
      logo: "🎵",
      description: "Generación musical IA",
      website: "suno.ai"
    },
    "Runway": {
      logo: "🎬",
      description: "IA para video creativo",
      website: "runwayml.com"
    },
    "Pika Labs": {
      logo: "⚡",
      description: "Video generativo",
      website: "pika.art"
    }
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-black text-gray-900 dark:text-white mb-4">
              🤖 Inteligencia Artificial en GPT-JDP
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              GPT-JDP integra proveedores de IA reconocidos para apoyar generación de texto, imágenes,
              chatbots y módulos informativos. Los servicios de voz, audio, música y video se encuentran
              actualmente <strong>en integración</strong> y se irán activando progresivamente.
            </p>
          </div>

          {/* Aviso de disponibilidad */}
          <div className="max-w-4xl mx-auto mb-8 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200 leading-relaxed">
              <strong>Aviso importante:</strong> La disponibilidad de modelos y versiones puede variar según integración
              técnica, proveedor, costos, región, actualizaciones de los propios proveedores y condiciones comerciales vigentes.
              Los nombres de modelos y marcas pertenecen a sus respectivos titulares.
              Última revisión de esta lista: <strong>febrero 2026</strong>.
            </p>
          </div>

          {/* Tabla de Modelos por Servicio */}
          <div className="bg-white dark:bg-[#1a2530] rounded-xl p-8 shadow-lg mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Modelos IA por Servicio
            </h2>
            
            <div className="space-y-6">
              {aiModels.map((service, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 dark:border-[#283039] rounded-lg p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-3xl text-primary">
                        {service.icon}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {service.service}
                      </h3>
                    </div>
                    {service.status && (
                      <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded-full text-sm font-medium whitespace-nowrap">
                        {service.status === 'en-integracion' ? 'En integración' : service.status}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {service.models.map((model, idx) => (
                      <div 
                        key={idx}
                        className="bg-gray-50 dark:bg-[#283039] rounded-lg p-4"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{providers[model.provider]?.logo}</span>
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white text-sm">
                              {model.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {model.provider}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {model.capabilities}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Proveedores */}
          <div className="bg-white dark:bg-[#1a2530] rounded-xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Nuestros Proveedores de IA
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(providers)
                .filter(([name]) => aiModels.some(s => s.models.some(m => m.provider === name)))
                .map(([name, info]) => (
                <div 
                  key={name}
                  className="border border-gray-200 dark:border-[#283039] rounded-lg p-6 hover:border-primary transition-colors"
                >
                  <div className="text-center mb-3">
                    <span className="text-5xl">{info.logo}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">
                    {name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-3">
                    {info.description}
                  </p>
                  <p className="text-xs text-center text-primary">
                    {info.website}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-6">
            <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-2">
              💡 ¿Por qué múltiples modelos?
            </h3>
            <p className="text-blue-800 dark:text-blue-300 text-sm">
              Cada proveedor y cada versión de modelo tiene fortalezas distintas: <span translate="no">GPT-5.2</span> y
              <span translate="no"> GPT-4.1</span> aportan razonamiento amplio, <span translate="no">Claude Sonnet 4.5</span> destaca
              en análisis y redacción extensa, <span translate="no">Gemini 3</span> ofrece multimodalidad y contexto amplio,
              y <span translate="no">DeepSeek V3</span> aporta capacidades específicas en código. Combinar varios
              proveedores permite orientar cada tarea al modelo más adecuado, dentro de la disponibilidad
              y condiciones comerciales vigentes.
            </p>
          </div>
        </div>
        
        <Footer />
      </div>
    </div>
  );
};

export default AIModels;
